import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useEffect, useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  eventDelete,
  eventList,
  eventOrdering,
  eventStatusChange,
} from "../../../../services/eventService";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import ManageEventDrawer from "../../../../components/sections/admin/Event/Drawer/ManageEventDrawer";
import Indentification from "../../../../components/elements/Indentification";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import DatePicker from "../../../../components/form/DatePicker";
import EventDetailsDrawer from "../../../../components/sections/admin/Event/Drawer/EventDetailsDrawer";
import { organizationList } from "../../../../services/organizationService";
import { useSelector } from "react-redux";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Enhanced styling functions for badges
const getLocationStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();
  const locationStatusStyles = {
    online: {
      backgroundColor: "#D1FAE5",
      color: "#047857",
      borderColor: "#10B981",
    },
    offline: {
      backgroundColor: "#DBEAFE",
      color: "#1D4ED8",
      borderColor: "#3B82F6",
    },
    notspecified: {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    },
  };

  return (
    locationStatusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const getVisibilityStyle = (status) => {
  const statusLower = status?.toLowerCase();
  const visibilityStyles = {
    public: {
      backgroundColor: "#D1FAE5",
      color: "#047857",
      borderColor: "#10B981",
    },
    private: {
      backgroundColor: "#FEE2E2",
      color: "#DC2626",
      borderColor: "#EF4444",
    },
    password: {
      backgroundColor: "#FEF3C7",
      color: "#D97706",
      borderColor: "#F59E0B",
    },
  };

  return (
    visibilityStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const getPublishedStyle = (published) => {
  return published
    ? {
        backgroundColor: "#D1FAE5",
        color: "#047857",
        borderColor: "#10B981",
      }
    : {
        backgroundColor: "#FEE2E2",
        color: "#DC2626",
        borderColor: "#EF4444",
      };
};

const Event = () => {
  const { t } = useTranslation();
  const { organizations, selectedOrganization } = useOrganization();
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const { lang } = useSelector((state) => state?.langProvider);
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [detailModal, setDetailModal] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [organizationKeyword, setuserKeyword] = useState("");
  const organizationSearch = useDebounce(organizationKeyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });
  const [organizationFilters, setOrganizationFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["Event", filters, search, selectedOrganization],
    queryFn: async () => {
      const res = await eventList({
        organization: selectedOrganization,
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!selectedOrganization,
  });

  //   Event status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(
          ["Event", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === id
                  ? {
                      ...elm,
                      status: elm?.status === "active" ? "inactive" : "active",
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          }
        );
        const res = await eventStatusChange({
          ids: id,
          status: status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          queryClient.setQueryData(
            ["Event", filters, search, selectedOrganization],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = (stateData || []).map((elm) =>
                  elm?._id === payload?.id
                    ? {
                        ...elm,
                        status:
                          elm?.status === "active" ? "inactive" : "active",
                      }
                    : elm
                );
                return { ...state, data: stateData };
              }
            }
          );
        }
      },
      onError: (error) => {},
    });

  const { mutate: publishEventHandler } = useMutation({
    mutationFn: async (value) => {
      const res = await eventStatusChange({ ...value });
      return { res, value };
    },
    onSuccess: ({ res, value }) => {
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["Event", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                value?.ids?.includes(elm?._id)
                  ? {
                      ...elm,
                      published: value?.published,
                    }
                  : elm
              );
              return { ...state, data: stateData };
            }
          }
        );
      } else {
        toast.warning(res?.message);
      }
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: t("Event"),
      align: "left",
    },
    {
      _id: "CreatedBy",
      name: t("Created By"),
      align: "left",
    },
    {
      _id: "eventStart",
      name: t("Start Date"),
      align: "left",
      ...tableFilter({
        content: (
          <DatePicker
            range
            onChange={({ startDate, endDate }) => {
              if (!startDate && !endDate) {
                setFilters((pre) => {
                  delete pre?.startDate;
                  delete pre?.endDate;
                  return { ...pre };
                });
              } else {
                setFilters((pre) => ({ ...pre, startDate, endDate }));
              }
            }}
          />
        ),
      }),
    },
    {
      _id: 4,
      name: t("Location Status"),
      align: "left",
      ...tableFilter({
        filterKey: "locationStatus",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [
          { name: "online", value: "online", type: "checkbox" },
          { name: "offline", value: "offline", type: "checkbox" },
          { name: "notspecified", value: "notspecified", type: "checkbox" },
        ],
      }),
    },
    {
      _id: "visibility",
      name: t("Visibility"),
      align: "left",
      ...tableFilter({
        filterKey: "visibility",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [
          { name: "public", value: "public", type: "checkbox" },
          { name: "private", value: "private", type: "checkbox" },
          { name: "password", value: "password", type: "checkbox" },
        ],
      }),
    },
    {
      _id: "published",
      name: t("Published"),
      align: "center",
      ...tableFilter({
        filterKey: "published",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [
          { name: "published", value: true, type: "checkbox" },
          { name: "unpublished", value: false, type: "checkbox" },
        ],
      }),
    },
    {
      _id: 8,
      name: t("Action"),
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    const locationStyle = getLocationStatusStyle(item?.locationStatus);
    const visibilityStyle = getVisibilityStyle(item?.visibility);
    const publishedStyle = getPublishedStyle(item?.published);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[250px]",
          data: (
            <div className="flex items-center gap-2">
              <div className="flex items-start -space-x-4">
                <Indentification
                  className={"shadow-lg"}
                  type="square"
                  size="2xl"
                  image={item?.gallery && item?.gallery[0]?.url}
                />
              </div>
              <div className="!font-semibold">
                <div>{lngDetect(item?.title)}</div>
                <div className="font-normal text-text-secondary">
                  {dayjs(item?.createdAt).format(
                    "dddd, MMMM D, YYYY [at] h:mm A"
                  ) || "-"}
                </div>
              </div>
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.userData?.firstName || item?.userData?.lastName
              ? `${item?.userData?.firstName || ""} ${
                  item?.userData?.lastName || ""
                }`
              : "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.startDate
            ? dayjs(item?.startDate).format("MMM DD, YYYY")
            : "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 select-none hover:brightness-95 hover:scale-105`}
              style={{
                backgroundColor: locationStyle.backgroundColor,
                color: locationStyle.color,
                borderColor: locationStyle.borderColor,
              }}
              title={t("Click to toggle location status")}
            >
              {item?.locationStatus?.charAt(0)?.toUpperCase() +
                item?.locationStatus?.slice(1)}
            </span>
          ),
        },
        {
          _id: "visibility",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 select-none hover:brightness-95 hover:scale-105`}
              style={{
                backgroundColor: visibilityStyle.backgroundColor,
                color: visibilityStyle.color,
                borderColor: visibilityStyle.borderColor,
              }}
              title="Click to toggle visibility"
            >
              {item?.visibility?.charAt(0)?.toUpperCase() +
                item?.visibility?.slice(1)}
            </span>
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "center",
          cellClass: "!min-w-[120px]",
          data: (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 select-none hover:brightness-95 hover:scale-105`}
              style={{
                backgroundColor: publishedStyle.backgroundColor,
                color: publishedStyle.color,
                borderColor: publishedStyle.borderColor,
              }}
              title="Click to toggle published status"
            >
              {item?.published ? "Published" : "Unpublished"}
            </span>
          ),
        },
        {
          _id: 9,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "dashboard",
              name: t("Dashboard"),
              icon: "fa-regular fa-dashboard",
              onClick: () => {
                navigate(`/${lang}/user/myEvent/${item?.uid}`);
              },
              standout: false,
            },
            !item?.published && {
              _id: "published",
              name: t(item?.published ? "Unpublished" : "Published"),
              icon: item?.published
                ? "fa-regular fa-eye-slash"
                : "fa-regular fa-eye",
              onClick: () => {
                publishEventHandler({
                  ids: [item?._id],
                  published: !item?.published,
                });
              },
              standout: false,
            },
            {
              _id: "Tickets",
              name: t("Tickets"),
              icon: "fa-regular fa-ticket",
              onClick: () => {
                navigate(`/${lang}/user/events/tickets/${item?.uid}`);
              },
              standout: false,
            },
            {
              _id: "Detalis",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                navigate(`/${lang}/user/events?details=${item?.uid}`);
              },
              standout: false,
            },
            {
              _id: "Edit",
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                navigate(`/${lang}/user/events/event-manage/${item?.uid}`);
              },
              standout: false,
            },
            {
              _id: "Delete",
              name: t("Delete"),
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ]?.filter(Boolean),
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(
            ["Event", filters, search, selectedOrganization],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = [res?.data, ...stateData];
                return { ...state, data: stateData };
              }
            }
          );
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries([
              "Event",
              filters,
              search,
              selectedOrganization,
            ]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["Event", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          }
        );
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "Event",
            filters,
            search,
            selectedOrganization,
          ]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  useEffect(() => {
    const handleAddEvent = (e) => {
      pageManageResponse(e?.detail);
    };

    window.addEventListener("addEvent", handleAddEvent);

    return () => {
      window.removeEventListener("addEvent", handleAddEvent);
    };
  }, []);

  //   Event delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await eventDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(
          ["Event", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.filter(
                (item) => !payload?.includes(item?._id)
              );

              return { ...state, data: stateData };
            }
          }
        );

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "Event",
            filters,
            search,
            selectedOrganization,
          ]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {},
  });

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await eventOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(
          ["Event", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              return { ...state, data: oldData };
            }
          }
        );
        toast.warning(res?.message || res?.error);
      }
    },
  });

  useEffect(() => {
    if (queryObject?.details) {
      setDetailModal({ isOpen: true, data: { uid: queryObject?.details } });
    }
  }, [queryObject?.details]);

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-4 md:space-y-8 py-8 px-0 md:px-5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-2">
          <h2 className="text-xl md:text-2xl text-slate-900 font-bold font-Montserrat uppercase">
            {t("events overview")}
          </h2>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full">
              <Search
                placeholder={t("Search here")}
                divClasses={"border-slate-300 bg-slate-50"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["Event", filters, search, selectedOrganization],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          handleOrdering={(newData, oldData) =>
            handleOrdering({ newData, oldData })
          }
          isOrderingDisabled={false}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
      <ManageEventDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={pageManageResponse}
      />
      <EventDetailsDrawer
        open={detailModal?.isOpen}
        data={detailModal?.data}
        close={() => {
          setDetailModal({ isOpen: false, data: null });
          navigate(`/${lang}/user/events`);
        }}
        getResponse={pageManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Event;
